/**
 * Sales-facing Deal Type matching: product type + sale-type variant.
 *
 * Catalog is org-authored (keys, names, match tags). Incoming feeds only
 * supply signals — no manufacturer/dealer names and no product SKUs in code.
 */
import { fulfillmentKindFromSignals, hostFulfillmentKind } from "../ops/hostOrderFulfillment.js";
import type { HostFulfillmentKind } from "../ops/hostOrderFulfillment.js";
import { preferCanonicalFulfillmentProduct } from "./dealTypeMatchFulfillment.js";
import { inferSalePathKind, type SalePathKind } from "./saleTypeProviders.js";

export { dealTypeMatchFromUnknown, typeMatchFieldsFromState } from "./dealTypeMatchParse.js";

export const DEAL_TYPE_MATCH_STATUSES = ["matched", "product_only", "unresolved"] as const;
export type DealTypeMatchStatus = (typeof DEAL_TYPE_MATCH_STATUSES)[number];
export type DealTypeMatchSource = "auto" | "manual";
export type DealTypeMatchConfidence = "high" | "medium" | "none";

export type DealTypeMatchCatalogEntry = {
  id: string;
  type_key: string;
  display_name: string;
  is_active: boolean;
  aliases: string[];
};

export type DealTypeMatchLink = { product_type_id: string; sale_type_id: string };

export type DealTypeMatchCatalog = {
  organization_id: string;
  product_types: DealTypeMatchCatalogEntry[];
  sale_types: DealTypeMatchCatalogEntry[];
  links: DealTypeMatchLink[];
};

export type DealTypeMatchState = {
  status: DealTypeMatchStatus;
  source: DealTypeMatchSource;
  selling_org_id: string;
  product_type_id: string | null;
  sale_type_id: string | null;
  product_type_name: string | null;
  sale_type_name: string | null;
  product_confidence: DealTypeMatchConfidence;
  sale_confidence: DealTypeMatchConfidence;
  reason: string;
  /** Instance fulfillment; not a second product. Null on historical stamps. */
  fulfillment: HostFulfillmentKind | null;
};

const STOP = new Set(["sale", "type", "deal", "order", "the", "and", "for", "with", "a", "of"]);

function str(raw: unknown): string {
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  return "";
}

function parseTagList(v: unknown): string[] {
  if (Array.isArray(v)) {
    return v.map((x) => (typeof x === "string" ? x.trim() : "")).filter(Boolean);
  }
  if (typeof v === "string") {
    return v
      .split(/\n|,/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Product- and sale-type match tags (`match_tags` or legacy `match_aliases`). */
export function parseMatchAliases(metadata: Record<string, unknown> | null | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of [...parseTagList(metadata?.match_tags), ...parseTagList(metadata?.match_aliases)]) {
    const k = t.toLowerCase();
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

export function matchAliasesWithMetadata(
  existing: Record<string, unknown> | null | undefined,
  aliasesText: string
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...(existing ?? {}) };
  const aliases = parseTagList(aliasesText);
  if (aliases.length) {
    next.match_tags = aliases;
    next.match_aliases = aliases;
  } else {
    delete next.match_tags;
    delete next.match_aliases;
  }
  return next;
}

export function normalizeMatchText(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function matchTokens(raw: string): string[] {
  return normalizeMatchText(raw).split(" ").filter((t) => t && !STOP.has(t));
}

const SIGNAL_KEYS = [
  "deal_type",
  "product_type",
  "productType",
  "building_type",
  "category",
  "model",
  "payment_type",
  "paymentType",
  "purchase_type",
  "sale_type",
  "financing",
  "finance_type",
  "order_type",
  "condition",
] as const;

/** Generic payload → matcher signals. Does not infer product from model brand names. */
export function extractDealTypeMatchSignals(body: Record<string, unknown>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    const n = normalizeMatchText(raw);
    if (!n || seen.has(n)) return;
    seen.add(n);
    out.push(n);
  };
  for (const key of SIGNAL_KEYS) push(str(body[key]));
  const fulfillment = hostFulfillmentKind(body);
  if (fulfillment) push(fulfillment);
  return out;
}

export function catalogEntryFromRow(row: {
  id: string;
  type_key: string;
  display_name: string;
  is_active?: boolean;
  metadata?: Record<string, unknown> | null;
}): DealTypeMatchCatalogEntry {
  return {
    id: row.id,
    type_key: row.type_key,
    display_name: row.display_name,
    is_active: row.is_active !== false,
    aliases: parseMatchAliases(row.metadata),
  };
}

function needles(entry: DealTypeMatchCatalogEntry): string[] {
  const out = new Set<string>();
  const key = normalizeMatchText(entry.type_key.replace(/_/g, " "));
  const name = normalizeMatchText(entry.display_name);
  if (key) out.add(key);
  if (name) out.add(name);
  for (const a of entry.aliases) {
    const n = normalizeMatchText(a);
    if (n) out.add(n);
  }
  return [...out];
}

export function rankCatalogMatch(
  signals: string[],
  entry: DealTypeMatchCatalogEntry
): "exact" | "token" | null {
  if (!entry.is_active) return null;
  const sig = signals.map(normalizeMatchText).filter(Boolean);
  const n = needles(entry);
  for (const needle of n) {
    if (sig.includes(needle)) return "exact";
  }
  const sigTokens = new Set(sig.flatMap(matchTokens));
  const candTokens = new Set(n.flatMap(matchTokens));
  for (const t of candTokens) {
    if (t.length >= 3 && sigTokens.has(t)) return "token";
  }
  return null;
}

type LayerPick =
  | { kind: "hit"; entry: DealTypeMatchCatalogEntry; confidence: DealTypeMatchConfidence }
  | { kind: "ambiguous" }
  | { kind: "miss" };

function pickLayer(entries: DealTypeMatchCatalogEntry[], signals: string[]): LayerPick {
  const active = entries.filter((e) => e.is_active);
  const exact = active.filter((e) => rankCatalogMatch(signals, e) === "exact");
  if (exact.length === 1) return { kind: "hit", entry: exact[0], confidence: "high" };
  if (exact.length > 1) {
    const preferred = preferCanonicalFulfillmentProduct(exact, needles);
    if (preferred) return { kind: "hit", entry: preferred, confidence: "high" };
    return { kind: "ambiguous" };
  }
  const token = active.filter((e) => rankCatalogMatch(signals, e) === "token");
  if (token.length === 1) return { kind: "hit", entry: token[0], confidence: "medium" };
  if (token.length > 1) {
    const preferred = preferCanonicalFulfillmentProduct(token, needles);
    if (preferred) return { kind: "hit", entry: preferred, confidence: "medium" };
    return { kind: "ambiguous" };
  }
  return { kind: "miss" };
}

export function salePathKindsFromSignals(signals: string[]): SalePathKind[] {
  const t = signals.join(" ");
  const out: SalePathKind[] = [];
  if (/\brto\b|rent[\s-]?to[\s-]?own/.test(t)) out.push("rto");
  if (/\bfinance\b|\bloan\b/.test(t)) out.push("finance");
  if (/\bcash\b/.test(t)) out.push("cash");
  // Hosts often send payment_type=cash on an RTO/finance purchase.
  if (out.includes("rto") || out.includes("finance")) return out.filter((k) => k !== "cash");
  return out;
}

function pickSaleByPathKind(
  linked: DealTypeMatchCatalogEntry[],
  signals: string[]
): LayerPick {
  const kinds = salePathKindsFromSignals(signals);
  if (kinds.length !== 1) return { kind: "miss" };
  const kind = kinds[0];
  const hits = linked.filter((e) => inferSalePathKind(e.type_key, e.display_name) === kind);
  if (hits.length === 1) return { kind: "hit", entry: hits[0], confidence: "high" };
  if (hits.length > 1) return { kind: "ambiguous" };
  return { kind: "miss" };
}

function emptyState(
  sellingOrgId: string,
  reason: string,
  fulfillment: HostFulfillmentKind | null = null,
): DealTypeMatchState {
  return {
    status: "unresolved",
    source: "auto",
    selling_org_id: sellingOrgId,
    product_type_id: null,
    sale_type_id: null,
    product_type_name: null,
    sale_type_name: null,
    product_confidence: "none",
    sale_confidence: "none",
    reason,
    fulfillment,
  };
}

function withFulfillment(state: DealTypeMatchState, signals: string[]): DealTypeMatchState {
  return { ...state, fulfillment: fulfillmentKindFromSignals(signals) };
}

export function resolveDealTypeMatch(args: {
  catalog: DealTypeMatchCatalog;
  signals: string[];
  source?: DealTypeMatchSource;
}): DealTypeMatchState {
  const selling = args.catalog.organization_id;
  const source = args.source ?? "auto";
  const products = args.catalog.product_types.filter((p) => p.is_active);
  if (products.length === 0) {
    return withFulfillment(
      emptyState(selling, "No active product types on the selling org — author them in Deal Types."),
      args.signals,
    );
  }

  const productPick = pickLayer(products, args.signals);
  if (productPick.kind !== "hit") {
    const reason =
      productPick.kind === "ambiguous"
        ? "Multiple product types matched the feed — pick one on the deal."
        : "No product type matched with confidence — pick one on the deal.";
    return withFulfillment({ ...emptyState(selling, reason), source }, args.signals);
  }

  const linkedIds = new Set(
    args.catalog.links.filter((l) => l.product_type_id === productPick.entry.id).map((l) => l.sale_type_id)
  );
  const linkedSales = args.catalog.sale_types.filter((s) => s.is_active && linkedIds.has(s.id));
  let salePick = pickSaleByPathKind(linkedSales, args.signals);
  if (salePick.kind === "miss") salePick = pickLayer(linkedSales, args.signals);

  const productHigh = productPick.confidence === "high";
  const saleHigh = salePick.kind === "hit" && salePick.confidence === "high";

  if (productHigh && saleHigh && salePick.kind === "hit") {
    return withFulfillment(
      {
        status: "matched",
        source,
        selling_org_id: selling,
        product_type_id: productPick.entry.id,
        sale_type_id: salePick.entry.id,
        product_type_name: productPick.entry.display_name,
        sale_type_name: salePick.entry.display_name,
        product_confidence: "high",
        sale_confidence: "high",
        reason: "Product type and sale type both matched with high confidence.",
        fulfillment: null,
      },
      args.signals,
    );
  }

  const saleHint =
    salePick.kind === "ambiguous"
      ? "Product matched; sale type is ambiguous — pick the variant."
      : salePick.kind === "hit"
        ? "Product matched; sale type still needs a high-confidence (or manual) pick."
        : "Product matched; sale type is not matched — pick the variant.";

  return withFulfillment(
    {
      status: "product_only",
      source,
      selling_org_id: selling,
      product_type_id: productPick.entry.id,
      sale_type_id: null,
      product_type_name: productPick.entry.display_name,
      sale_type_name: null,
      product_confidence: productPick.confidence,
      sale_confidence: salePick.kind === "hit" ? salePick.confidence : "none",
      reason: productHigh ? saleHint : "Product type is only a medium-confidence hint — confirm both layers.",
      fulfillment: null,
    },
    args.signals,
  );
}

export function applyManualDealTypeMatch(args: {
  catalog: DealTypeMatchCatalog;
  productTypeId: string;
  saleTypeId: string | null;
  fulfillment?: HostFulfillmentKind | null;
}): DealTypeMatchState | { error: string } {
  const fulfillment = args.fulfillment ?? null;
  const product = args.catalog.product_types.find((p) => p.id === args.productTypeId && p.is_active);
  if (!product) return { error: "product type not found on this selling org" };
  if (!args.saleTypeId) {
    return {
      status: "product_only",
      source: "manual",
      selling_org_id: args.catalog.organization_id,
      product_type_id: product.id,
      sale_type_id: null,
      product_type_name: product.display_name,
      sale_type_name: null,
      product_confidence: "high",
      sale_confidence: "none",
      reason: "Product type set; sale type still needed.",
      fulfillment,
    };
  }
  const linked = args.catalog.links.some(
    (l) => l.product_type_id === product.id && l.sale_type_id === args.saleTypeId
  );
  const sale = args.catalog.sale_types.find((s) => s.id === args.saleTypeId && s.is_active);
  if (!sale || !linked) return { error: "sale type is not linked to that product type" };
  return {
    status: "matched",
    source: "manual",
    selling_org_id: args.catalog.organization_id,
    product_type_id: product.id,
    sale_type_id: sale.id,
    product_type_name: product.display_name,
    sale_type_name: sale.display_name,
    product_confidence: "high",
    sale_confidence: "high",
    reason: "Product type and sale type set on the deal.",
    fulfillment,
  };
}
